/**
 * Hooks react-query : SEULE porte d'entrée des écrans vers l'API.
 * Toutes les données sont scopées par l'organisation active (useOrgId).
 * Convention de clés : ['org', orgId, <ressource>, ...params].
 */
import { useEffect } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, keepPreviousData, type QueryKey } from '@tanstack/react-query';
import { useApi } from './ApiProvider';
import { useAuthStore, useOrgId } from '../auth/store';
import type { ApiClient } from './client';
import type {
  AuditListParams, DeviceListParams, ExportFormat, MemberListParams, Page, Device, PresenceEvent, SessionListParams,
  LoginRequest, RegisterRequest, VerifyTotpRequest, RatingListParams,
} from './types';

export const keys = {
  organizations: ['organizations'] as const,
  me: ['me'] as const,
  org: (org: string) => ['org', org] as const,
  settings: (org: string) => ['org', org, 'settings'] as const,
  dashboard: (org: string) => ['org', org, 'dashboard'] as const,
  devices: (org: string) => ['org', org, 'devices'] as const,
  devicesList: (org: string, p?: DeviceListParams) => ['org', org, 'devices', 'list', p ?? {}] as const,
  device: (org: string, id: string) => ['org', org, 'devices', 'detail', id] as const,
  groups: (org: string) => ['org', org, 'groups'] as const,
  members: (org: string) => ['org', org, 'members'] as const,
  membersList: (org: string, p?: MemberListParams) => ['org', org, 'members', 'list', p ?? {}] as const,
  roles: (org: string) => ['org', org, 'roles'] as const,
  invitations: (org: string) => ['org', org, 'invitations'] as const,
  policies: (org: string) => ['org', org, 'policies'] as const,
  policy: (org: string, id: string) => ['org', org, 'policies', id] as const,
  audit: (org: string) => ['org', org, 'audit'] as const,
  auditList: (org: string, p?: AuditListParams) => ['org', org, 'audit', 'list', p ?? {}] as const,
  sessions: (org: string) => ['org', org, 'sessions'] as const,
  sessionsList: (org: string, p?: SessionListParams) => ['org', org, 'sessions', 'list', p ?? {}] as const,
  session: (org: string, id: string) => ['org', org, 'sessions', 'detail', id] as const,
  ratings: (org: string) => ['org', org, 'ratings'] as const,
  ratingSummary: (org: string) => ['org', org, 'ratings', 'summary'] as const,
  technicianRatings: (org: string, id: string) => ['org', org, 'ratings', 'technician', id] as const,
  ratingsList: (org: string, p?: RatingListParams) => ['org', org, 'ratings', 'list', p ?? {}] as const,
  tokens: (org: string) => ['org', org, 'enrollment-tokens'] as const,
  releases: (org: string) => ['org', org, 'releases'] as const,
  latestAgent: (org: string) => ['org', org, 'latest-agent'] as const,
  subscription: (org: string) => ['org', org, 'subscription'] as const,
  invoices: (org: string) => ['org', org, 'invoices'] as const,
  plans: ['plans'] as const,
  integrations: (org: string) => ['org', org, 'integrations'] as const,
  apiKeys: (org: string) => ['org', org, 'api-keys'] as const,
};

/* ---------- Authentification ---------- */

export function useRegister() {
  const api = useApi();
  return useMutation({ mutationFn: (req: RegisterRequest) => api.register(req) });
}

export function useLogin() {
  const api = useApi();
  return useMutation({ mutationFn: (req: LoginRequest) => api.login(req) });
}

export function useVerifyTotp() {
  const api = useApi();
  return useMutation({ mutationFn: (req: VerifyTotpRequest) => api.verifyTotp(req) });
}

export function useOrganizations() {
  const api = useApi();
  const token = useAuthStore((s) => s.token);
  return useQuery({ queryKey: keys.organizations, queryFn: () => api.listOrganizations(), enabled: Boolean(token) });
}

/* ---------- Helpers ---------- */

/** Mutation scopée à l'organisation active ; invalide les clés indiquées après succès. */
function useOrgMutation<TVars, TResult>(
  fn: (api: ApiClient, org: string, vars: TVars) => Promise<TResult>,
  invalidate: (org: string) => QueryKey[],
) {
  const api = useApi();
  const org = useOrgId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: TVars) => fn(api, org, vars),
    onSuccess: async () => {
      // Toute action d'administration est auditée : on rafraîchit aussi le journal
      await Promise.all([...invalidate(org), keys.audit(org)].map((queryKey) => qc.invalidateQueries({ queryKey })));
    },
  });
}

/* ---------- Organisation et tableau de bord ---------- */

export function useOrgSettings() {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: keys.settings(org), queryFn: () => api.getOrganizationSettings(org) });
}

export function useUpdateOrgSettings() {
  const qc = useQueryClient();
  return useOrgMutation(
    (api, org, patch: Parameters<ApiClient['updateOrganizationSettings']>[1]) => api.updateOrganizationSettings(org, patch),
    (org) => {
      void qc.invalidateQueries({ queryKey: keys.organizations });
      return [keys.settings(org)];
    },
  );
}

export function useDashboard() {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: keys.dashboard(org), queryFn: () => api.getDashboard(org), refetchInterval: 60_000 });
}

/* ---------- Parc ---------- */

export function useDevices(params?: DeviceListParams, options?: { enabled?: boolean }) {
  const api = useApi();
  const org = useOrgId();
  return useQuery({
    queryKey: keys.devicesList(org, params),
    queryFn: () => api.listDevices(org, params),
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  });
}

export function useDevice(id: string | undefined) {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: keys.device(org, id ?? ''), queryFn: () => api.getDevice(org, id!), enabled: Boolean(id) });
}

export function useDeviceGroups() {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: keys.groups(org), queryFn: () => api.listDeviceGroups(org) });
}

export const useUpdateDevice = () =>
  useOrgMutation(
    (api, org, v: { id: string; patch: Parameters<ApiClient['updateDevice']>[2] }) => api.updateDevice(org, v.id, v.patch),
    (org) => [keys.devices(org), keys.groups(org)],
  );
export const useRevokeDevice = () =>
  useOrgMutation((api, org, id: string) => api.revokeDevice(org, id), (org) => [keys.devices(org), keys.groups(org), keys.dashboard(org)]);
/* Zones fleet / people : mise à jour d'agent, seuils d'alerte, SSO */
export const useRequestDeviceUpdate = () =>
  useOrgMutation((api, org, id: string) => api.requestDeviceUpdate(org, id), (org) => [keys.devices(org), keys.dashboard(org)]);
export function useAlertSettings() {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: ['org', org, 'alert-settings'], queryFn: () => api.getAlertSettings(org) });
}
export const useUpdateAlertSettings = () =>
  useOrgMutation(
    (api, org, patch: Parameters<ApiClient['updateAlertSettings']>[1]) => api.updateAlertSettings(org, patch),
    (org) => [['org', org, 'alert-settings'], keys.devices(org), keys.dashboard(org)],
  );
export function useSsoConfig() {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: ['org', org, 'sso'], queryFn: () => api.getSsoConfig(org) });
}
export const useSaveSsoConfig = () =>
  useOrgMutation((api, org, config: Parameters<ApiClient['saveSsoConfig']>[1]) => api.saveSsoConfig(org, config), (org) => [['org', org, 'sso'], keys.integrations(org)]);
export const useCreateGroup = () =>
  useOrgMutation((api, org, input: Parameters<ApiClient['createDeviceGroup']>[1]) => api.createDeviceGroup(org, input), (org) => [keys.groups(org)]);
export const useUpdateGroup = () =>
  useOrgMutation(
    (api, org, v: { id: string; patch: Parameters<ApiClient['updateDeviceGroup']>[2] }) => api.updateDeviceGroup(org, v.id, v.patch),
    (org) => [keys.groups(org)],
  );
export const useDeleteGroup = () =>
  useOrgMutation((api, org, id: string) => api.deleteDeviceGroup(org, id), (org) => [keys.groups(org), keys.devices(org)]);

/**
 * Présence en temps réel : à monter une fois dans un écran qui affiche des appareils.
 * Applique les événements directement dans le cache react-query (aucun refetch).
 */
export function usePresenceSubscription(enabled = true) {
  const api = useApi();
  const org = useOrgId();
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    const unsubscribe = api.subscribePresence(org, (events: PresenceEvent[]) => {
      const byId = new Map(events.map((e) => [e.deviceId, e]));
      const patch = (d: Device): Device => {
        const e = byId.get(d.id);
        return e ? { ...d, status: e.status, lastSeenAt: e.at } : d;
      };
      qc.setQueriesData<Page<Device>>({ queryKey: [...keys.devices(org), 'list'] }, (old) => (old ? { ...old, items: old.items.map(patch) } : old));
      qc.setQueriesData<Device>({ queryKey: [...keys.devices(org), 'detail'] }, (old) => (old ? patch(old) : old));
      void qc.invalidateQueries({ queryKey: keys.dashboard(org) });
    });
    return unsubscribe;
  }, [api, org, qc, enabled]);
}

/* ---------- Personnes ---------- */

export function useMembers(params?: MemberListParams) {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: keys.membersList(org, params), queryFn: () => api.listMembers(org, params), placeholderData: keepPreviousData });
}

export function useRoles() {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: keys.roles(org), queryFn: () => api.listRoles(org) });
}

export function useInvitations() {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: keys.invitations(org), queryFn: () => api.listInvitations(org) });
}

export const useUpdateMember = () =>
  useOrgMutation(
    (api, org, v: { id: string; patch: Parameters<ApiClient['updateMember']>[2] }) => api.updateMember(org, v.id, v.patch),
    (org) => [keys.members(org), keys.roles(org)],
  );
export const useRemoveMember = () =>
  useOrgMutation((api, org, id: string) => api.removeMember(org, id), (org) => [keys.members(org), keys.roles(org)]);
export const useSaveRole = () =>
  useOrgMutation((api, org, role: Parameters<ApiClient['saveRole']>[1]) => api.saveRole(org, role), (org) => [keys.roles(org)]);
export const useDeleteRole = () => useOrgMutation((api, org, id: string) => api.deleteRole(org, id), (org) => [keys.roles(org)]);
export const useCreateInvitation = () =>
  useOrgMutation((api, org, req: Parameters<ApiClient['createInvitation']>[1]) => api.createInvitation(org, req), (org) => [keys.invitations(org)]);
export const useRevokeInvitation = () =>
  useOrgMutation((api, org, id: string) => api.revokeInvitation(org, id), (org) => [keys.invitations(org)]);
export const useResendInvitation = () =>
  useOrgMutation((api, org, id: string) => api.resendInvitation(org, id), (org) => [keys.invitations(org)]);

/* ---------- Politiques ---------- */

export function usePolicies() {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: keys.policies(org), queryFn: () => api.listPolicies(org) });
}

export function usePolicy(id: string | undefined) {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: keys.policy(org, id ?? ''), queryFn: () => api.getPolicy(org, id!), enabled: Boolean(id) });
}

export const useCreatePolicy = () =>
  useOrgMutation((api, org, input: Parameters<ApiClient['createPolicy']>[1]) => api.createPolicy(org, input), (org) => [keys.policies(org)]);
export const useUpdatePolicy = () =>
  useOrgMutation(
    (api, org, v: { id: string; input: Parameters<ApiClient['updatePolicy']>[2] }) => api.updatePolicy(org, v.id, v.input),
    (org) => [keys.policies(org)],
  );
export const useDeletePolicy = () => useOrgMutation((api, org, id: string) => api.deletePolicy(org, id), (org) => [keys.policies(org)]);
/** Aperçu de l'effet d'une politique avant application (pas d'invalidation : lecture seule). */
export function usePreviewPolicy() {
  const api = useApi();
  const org = useOrgId();
  return useMutation({ mutationFn: (input: Parameters<ApiClient['previewPolicy']>[1]) => api.previewPolicy(org, input) });
}

/** Historique des versions d'une politique. */
export function usePolicyVersions(id: string | undefined) {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: [...keys.policy(org, id ?? ''), 'versions'], queryFn: () => api.listPolicyVersions(org, id!), enabled: Boolean(id) });
}
/** Retour arrière : restaure une ancienne version sous forme de nouvelle version. */
export const useRollbackPolicy = () =>
  useOrgMutation((api, org, v: { id: string; version: number }) => api.rollbackPolicy(org, v.id, v.version), (org) => [keys.policies(org)]);
/** Progression de diffusion ; se rafraîchit tant que des agents en ligne n'ont pas accusé réception. */
export function usePolicyDelivery(id: string | undefined) {
  const api = useApi();
  const org = useOrgId();
  return useQuery({
    queryKey: [...keys.policy(org, id ?? ''), 'delivery'],
    queryFn: () => api.getPolicyDelivery(org, id!),
    enabled: Boolean(id),
    refetchInterval: (q) => (q.state.data && q.state.data.pending > 0 ? 5_000 : false),
  });
}

/* ---------- Audit et sessions ---------- */

export function useAuditEvents(params?: AuditListParams) {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: keys.auditList(org, params), queryFn: () => api.listAuditEvents(org, params), placeholderData: keepPreviousData });
}

export function useExportAudit() {
  const api = useApi();
  const org = useOrgId();
  return useMutation({ mutationFn: (v: { format: ExportFormat; params?: AuditListParams }) => api.exportAudit(org, v.format, v.params) });
}

export function useSessions(params?: SessionListParams) {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: keys.sessionsList(org, params), queryFn: () => api.listSessions(org, params), placeholderData: keepPreviousData });
}

export function useSession(id: string | undefined) {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: keys.session(org, id ?? ''), queryFn: () => api.getSession(org, id!), enabled: Boolean(id) });
}

/** Lecture d'un enregistrement : journalisée, refusée sans consentement du client. */
export function useAccessRecording() {
  const api = useApi();
  const org = useOrgId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { sessionId: string; reason: string }) => api.accessRecording(org, v.sessionId, v.reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.audit(org) }),
  });
}

/* ---------- Satisfaction ---------- */

export function useRatingSummary() {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: keys.ratingSummary(org), queryFn: () => api.getRatingSummary(org) });
}

export function useTechnicianRatings(technicianId: string | undefined) {
  const api = useApi();
  const org = useOrgId();
  return useQuery({
    queryKey: keys.technicianRatings(org, technicianId ?? ''),
    queryFn: () => api.getTechnicianRatings(org, technicianId!),
    enabled: Boolean(technicianId),
  });
}

/** Notes paginées par curseur : `fetchNextPage()` charge la suite (« Afficher plus »). */
export function useRatings(params?: Omit<RatingListParams, 'cursor'>) {
  const api = useApi();
  const org = useOrgId();
  return useInfiniteQuery({
    queryKey: keys.ratingsList(org, params),
    queryFn: ({ pageParam }) => api.listRatings(org, { ...params, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/* ---------- Déploiement ---------- */

export function useEnrollmentTokens() {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: keys.tokens(org), queryFn: () => api.listEnrollmentTokens(org) });
}

export function useReleases() {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: keys.releases(org), queryFn: () => api.listReleases(org) });
}

/** Dernière version stable de l'agent (serveur) ; `null` tant qu'inconnue ou si rien n'est publié. */
export function useLatestAgent(): string | null {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: keys.latestAgent(org), queryFn: () => api.getLatestAgent(org) }).data ?? null;
}

export const useCreateEnrollmentToken = () =>
  useOrgMutation((api, org, req: Parameters<ApiClient['createEnrollmentToken']>[1]) => api.createEnrollmentToken(org, req), (org) => [keys.tokens(org)]);
export const useRevokeEnrollmentToken = () =>
  useOrgMutation((api, org, id: string) => api.revokeEnrollmentToken(org, id), (org) => [keys.tokens(org)]);

/* ---------- Facturation ---------- */

export function useSubscription() {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: keys.subscription(org), queryFn: () => api.getSubscription(org) });
}

/** Catalogue des plans (identique pour toutes les organisations). */
export function usePlans() {
  const api = useApi();
  return useQuery({ queryKey: keys.plans, queryFn: () => api.listPlans(), staleTime: 5 * 60_000 });
}

export function useInvoices() {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: keys.invoices(org), queryFn: () => api.listInvoices(org) });
}

/** Démarre l'essai PRO (une fois par organisation) ; rafraîchit l'abonnement. */
export const useStartTrial = () =>
  useOrgMutation((api, org, _: void) => api.startTrial(org), (org) => [keys.subscription(org), keys.dashboard(org)]);

export const useUpdateSubscription = () =>
  useOrgMutation(
    (api, org, patch: Parameters<ApiClient['updateSubscription']>[1]) => api.updateSubscription(org, patch),
    (org) => [keys.subscription(org), keys.invoices(org), keys.dashboard(org)],
  );

export function usePaymentMethods() {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: [...keys.subscription(org), 'payment-methods'], queryFn: () => api.listPaymentMethods(org) });
}

const billingKeys = (org: string) => [keys.subscription(org), keys.invoices(org), keys.dashboard(org)];
export const useAddPaymentMethod = () =>
  useOrgMutation((api, org, req: Parameters<ApiClient['addPaymentMethod']>[1]) => api.addPaymentMethod(org, req), billingKeys);
export const useRemovePaymentMethod = () => useOrgMutation((api, org, id: string) => api.removePaymentMethod(org, id), billingKeys);
export const useSetDefaultPaymentMethod = () => useOrgMutation((api, org, id: string) => api.setDefaultPaymentMethod(org, id), billingKeys);
export const usePurchaseAiCredits = () =>
  useOrgMutation((api, org, req: Parameters<ApiClient['purchaseAiCredits']>[1]) => api.purchaseAiCredits(org, req), billingKeys);
export const useRetryPayment = () => useOrgMutation((api, org, _: void) => api.retryPayment(org), billingKeys);

/* ---------- Paramètres ---------- */

export function useIntegrations() {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: keys.integrations(org), queryFn: () => api.listIntegrations(org) });
}

export function useApiKeys() {
  const api = useApi();
  const org = useOrgId();
  return useQuery({ queryKey: keys.apiKeys(org), queryFn: () => api.listApiKeys(org) });
}

export const useSetIntegration = () =>
  useOrgMutation(
    (api, org, v: { kind: Parameters<ApiClient['setIntegrationConnected']>[1]; connected: boolean }) => api.setIntegrationConnected(org, v.kind, v.connected),
    (org) => [keys.integrations(org)],
  );
export const useCreateApiKey = () =>
  useOrgMutation((api, org, req: Parameters<ApiClient['createApiKey']>[1]) => api.createApiKey(org, req), (org) => [keys.apiKeys(org)]);
export const useRevokeApiKey = () => useOrgMutation((api, org, id: string) => api.revokeApiKey(org, id), (org) => [keys.apiKeys(org)]);
